import { BRAND } from "@/lib/api";
import { ShieldCheck, Truck, RotateCcw, AlertTriangle, Heart, Lock, Scale, FileText, Copyright } from "lucide-react";

const EFFECTIVE = "Effective date: " + new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

const SECTIONS = [
  {
    id: "orders", icon: FileText, title: "Orders & Payment",
    points: [
      "All prices are listed in U.S. dollars. Payment is processed securely through Stripe; we never see or store your full card details.",
      "Placing an order is an offer to purchase. We reserve the right to accept, decline, limit, or cancel any order at our discretion — including for suspected fraud, pricing errors, or stock issues.",
      "An order is only confirmed once payment is successfully captured and you receive a confirmation. We may verify billing and shipping details before fulfillment.",
    ],
  },
  {
    id: "shipping", icon: Truck, title: "Shipping & Delivery",
    points: [
      "We carefully prepare and ship each order by hand. Processing typically takes 3–5 business days before dispatch; estimated delivery is 5–7 business days after dispatch, depending on the carrier and destination.",
      "Once a package leaves our hands and is handed to the carrier, responsibility for transit and delivery passes to the carrier. We are not liable for carrier delays, weather, or incorrect addresses provided at checkout.",
      "Please double-check your shipping address. We are not responsible for orders shipped to an incorrect or incomplete address entered by the customer. Re-shipping may incur additional cost.",
      "If a package is marked delivered but you cannot locate it, please contact the carrier first; we will assist where we reasonably can.",
    ],
  },
  {
    id: "returns", icon: RotateCcw, title: "Returns, Refunds & Cancellations",
    points: [
      "Because our gifts are curated, handmade, and often personalized, many items are final sale and cannot be returned for hygiene and customization reasons (this includes Build Your Own Box, personalized notes, and digital downloads).",
      "Digital products (printables/downloads) are non-refundable once the download has been accessed.",
      "Order changes or cancellations may only be requested within 12 hours of ordering and before the order has entered preparation. After that, we cannot guarantee changes.",
      "If you are unhappy with your order, please reach out within 7 days of delivery and we will do our best to make it right. Approved refunds (if any) are issued to the original payment method and may exclude shipping costs.",
    ],
  },
  {
    id: "damaged", icon: ShieldCheck, title: "Damaged or Lost Items",
    points: [
      "If your order arrives damaged, please email us within 48 hours of delivery with photos of the item and packaging so we can help resolve it.",
      "Replacements or store credit for verified damage are handled case by case. We may request the damaged item or photos before issuing any remedy.",
    ],
  },
  {
    id: "fraud", icon: AlertTriangle, title: "Fraud Prevention & Chargebacks",
    points: [
      "To protect our small business, we actively screen orders for fraud. We reserve the right to cancel and refund any order we reasonably believe to be fraudulent, unauthorized, or high-risk.",
      "We may request identity or billing verification before shipping. Orders that cannot be verified may be cancelled and refunded.",
      "Filing a false or abusive chargeback (for example, claiming non-receipt of a delivered order, or disputing a clearly fulfilled order) is considered fraud. We retain order, tracking, and communication records and will contest illegitimate chargebacks with this evidence.",
      "If you have any issue with an order, please contact us first — we are always willing to work toward a fair resolution before any dispute is filed.",
    ],
  },
  {
    id: "conduct", icon: Heart, title: "Customer Conduct & Communication",
    points: [
      "We treat every customer with warmth and respect, and we kindly ask for the same in return. We reserve the right to decline service to, or cancel orders from, anyone who is abusive, threatening, or harassing toward our team.",
      "We do our best to respond to all messages with care. Please allow a reasonable time for a response, especially during busy seasons.",
      "Reviews and feedback are always welcome. We ask that they be honest and fair; we reserve the right to address false or defamatory statements.",
    ],
  },
  {
    id: "privacy", icon: Lock, title: "Privacy",
    points: [
      "We collect only the information needed to fulfill your order and communicate with you (such as your name, contact details, shipping address, and order history).",
      "Payment information is handled directly by Stripe under their security standards; we do not store your card numbers.",
      "We do not sell your personal information. We may use your details to process orders, provide support, prevent fraud, and — only if you opt in — send occasional updates.",
    ],
  },
  {
    id: "ip", icon: Copyright, title: "Intellectual Property & Copyright",
    points: [
      "All original floral arrangements, product photography, and custom resin sculptural designs displayed on this website are protected under federal copyright law. Unauthorized reproduction or imitation is strictly prohibited.",
      "All original 3D floral resin sculptures, custom gift box arrangements, website imagery, text, graphics, logos, and product designs displayed on this website are the exclusive property of " + BRAND.legal + " and are protected under United States and international copyright laws.",
      "No content on this site may be copied, reproduced, republished, modified, distributed, imitated, or used to create derivative works — in whole or in part — without our prior written permission. This includes scraping, screenshotting for commercial use, and AI training or ingestion.",
      "The Hope & Favor name, logo, and brand marks are trademarks of " + BRAND.legal + ". Any unauthorized use of our brand identity is prohibited.",
      "We actively monitor for unauthorized use of our work. Infringement may result in takedown requests (including DMCA notices) and legal action. To request licensing or report misuse, please contact us at " + BRAND.email + ".",
    ],
  },
  {
    id: "liability", icon: Scale, title: "Disclaimers & Limitation of Liability",
    points: [
      "Our gifts are intended to encourage and comfort. They are not medical, psychological, financial, or professional advice, and any faith-inspired content is offered as encouragement only.",
      "To the fullest extent permitted by law, Hope & Favor, LLC is not liable for indirect, incidental, or consequential damages arising from the use of our products or website. Our total liability for any claim will not exceed the amount you paid for the order in question.",
      "We may update these policies at any time; the version posted here with the current effective date governs your order. Continued use of the site means you accept the latest version.",
    ],
  },
];

export default function Policies() {
  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 py-20">
      <div className="text-center max-w-2xl mx-auto">
        <span className="gh-overline">Our Policies</span>
        <h1 className="font-heading text-5xl sm:text-6xl text-[#3A3632] mt-4">Caring for you — and for us.</h1>
        <div className="gh-hairline my-7 max-w-[120px] mx-auto" />
        <p className="text-lg text-[#6B655F] leading-relaxed font-light">
          These policies help us serve you with clarity and kindness, and protect our small business so we can keep doing what we love. If anything is ever unclear, please reach out — we are so grateful you are here.
        </p>
        <p className="text-sm text-[#8A8175] mt-4">{EFFECTIVE}</p>
      </div>

      <div className="grid lg:grid-cols-[220px_1fr] gap-12 mt-16">
        <nav className="hidden lg:block sticky top-32 h-fit space-y-2" data-testid="policies-nav">
          {SECTIONS.map((s) => (
            <a key={s.id} href={`#${s.id}`} className="block text-sm text-[#6B655F] hover:text-[#8E9A86] transition-colors gh-link-underline w-fit">{s.title}</a>
          ))}
        </nav>

        <div className="space-y-14">
          {SECTIONS.map((s) => (
            <section key={s.id} id={s.id} data-testid={`policy-${s.id}`} className="scroll-mt-32">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-11 w-11 gh-octagon bg-[#8E9A86]/12 flex items-center justify-center shrink-0"><s.icon className="h-5 w-5 text-[#8E9A86]" /></div>
                <h2 className="font-heading text-3xl text-[#3A3632]">{s.title}</h2>
              </div>
              <ul className="space-y-3">
                {s.points.map((p) => (
                  <li key={p} className="flex gap-3 text-[#6B655F] leading-relaxed">
                    <span className="mt-2.5 h-1.5 w-1.5 rounded-full bg-[#D9CBA0] shrink-0" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </section>
          ))}

          <section className="bg-[#F2EFEB] border border-[#D9CBA0]/40 p-8">
            <h2 className="font-heading text-2xl text-[#3A3632] mb-3">Questions about a policy?</h2>
            <p className="text-[#6B655F] leading-relaxed">We're always happy to talk it through. Reach {BRAND.owner} at <a href={`mailto:${BRAND.email}`} className="text-[#8E9A86] underline">{BRAND.email}</a> or <a href={`tel:${BRAND.phone}`} className="text-[#8E9A86] underline">{BRAND.phone}</a>.</p>
            <p className="text-xs text-[#8A8175] mt-4">These policies are provided as general terms for {BRAND.legal}. They are not legal advice; we recommend having them reviewed by an attorney for your state and situation.</p>
          </section>
        </div>
      </div>
    </div>
  );
}
